import { Loader2, Lock, Plus } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { smsTravado, type DestinoProposta } from '@/lib/leadSmsApi';
import { formatarTelefone } from '@/utils/formatters';
import { LINK_SMS_EXEMPLO, LIMITE_SMS, tamanhoSms, semAcento, type ModeloSmsProposta } from '@/lib/propostaSmsModelos';

function dataHora(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/** A trava do SMS é de 7 dias a partir do último envio. */
function liberaEm(iso: string): string {
  return new Date(new Date(iso).getTime() + 7 * 24 * 60 * 60 * 1000)
    .toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/** De onde veio cada destino, em palavra que o consultor entende. */
function rotuloFonte(fonte: string): string {
  if (fonte === 'linha_conta') return 'linha da conta';
  if (fonte === 'contato_real') return 'contato real';
  if (fonte.includes('socio')) return 'sócio';
  if (fonte.startsWith('assertiva')) return 'empresa';
  if (fonte === 'complementar' || fonte.startsWith('credlink')) return 'complementar';
  if (fonte.startsWith('cliente')) return 'cliente';
  if (fonte.startsWith('lead')) return 'lead';
  return fonte.replace(/_/g, ' ');
}

function rotulosUnicos(fontes: string[]): string[] {
  const ordem = ['linha da conta', 'contato real', 'sócio', 'empresa', 'cliente', 'lead', 'complementar'];
  const set = Array.from(new Set(fontes.map(rotuloFonte)));
  const posicao = (r: string) => (ordem.includes(r) ? ordem.indexOf(r) : 99);
  return set.sort((a, b) => posicao(a) - posicao(b));
}

interface ListaProps {
  canal: 'email' | 'sms';
  destinos: DestinoProposta[];
  marcados: string[];
  onChange: (marcados: string[]) => void;
  carregando: boolean;
  erro?: string | null;
}

export function ListaDestinosProposta({ canal, destinos, marcados, onChange, carregando, erro }: ListaProps) {
  const contatoReal = destinos.some((d) => d.regra === 'contato_real');
  const regra = canal === 'email'
    ? (contatoReal ? 'Contato Real preenchido · vai só para ele' : 'Sem Contato Real · vai para todos os e-mails do CNPJ (o da Receita só quando não há outro)')
    : (contatoReal ? 'Linhas da conta + o celular do Contato Real' : 'Linhas da conta + todos os celulares do CNPJ (sócios, empresa, lead)');
  const travadosTodos = canal === 'sms' && destinos.length > 0 && destinos.every((d) => smsTravado(d));
  const ultimoEnvio = destinos.map((d) => d.ultimo_envio_em).filter(Boolean).sort().pop() || null;
  const alternar = (destino: string, marcar: boolean) =>
    onChange(marcar ? Array.from(new Set([...marcados, destino])) : marcados.filter((m) => m !== destino));

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center gap-2 border-b border-border px-3 py-1.5">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {canal === 'email' ? 'E-mails' : 'Celulares'} · {marcados.length} de {destinos.length}
        </span>
        {destinos.length > 1 && (
          <div className="ml-auto flex gap-2 text-[10.5px] font-semibold text-primary">
            <button type="button" onClick={() => onChange(destinos.filter((d) => !smsTravado(d)).map((d) => d.destino))}>todos</button>
            <button type="button" onClick={() => onChange([])}>nenhum</button>
          </div>
        )}
      </div>
      <p className="px-3 pt-1.5 text-[10px] text-muted-foreground">{regra}</p>
      {travadosTodos && (
        <p className="mx-3 mt-1.5 rounded border border-primary/30 bg-primary/5 px-2 py-1 text-[10.5px] font-semibold text-primary">
          Todos os celulares já receberam SMS nos últimos 7 dias. O próximo envio libera em {liberaEm(ultimoEnvio!)}.
        </p>
      )}
      <div className="max-h-44 overflow-y-auto px-1.5 py-1">
        {carregando ? (
          <div className="flex items-center gap-2 px-1.5 py-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> buscando os contatos deste cliente…
          </div>
        ) : erro ? (
          <p className="px-1.5 py-2 text-xs text-destructive">{erro}</p>
        ) : destinos.length === 0 ? (
          <p className="px-1.5 py-2 text-xs text-muted-foreground">
            {canal === 'email' ? 'Nenhum e-mail encontrado para este cliente.' : 'Nenhum celular encontrado para este cliente.'}
          </p>
        ) : destinos.map((d) => {
          const travado = smsTravado(d);
          const marcado = !travado && marcados.includes(d.destino);
          const quem = d.ultimo_envio_por ? String(d.ultimo_envio_por).split(/\s+/)[0] : '';
          const enviado = d.ultimo_envio_em
            ? `enviado ${dataHora(d.ultimo_envio_em)}${quem ? ` · ${quem.charAt(0) + quem.slice(1).toLowerCase()}` : ''}`
            : null;
          return (
            <label key={d.destino} className={`flex items-center gap-2 rounded px-1.5 py-1 ${travado ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-muted/60'}`}>
              <Checkbox checked={marcado} disabled={travado} onCheckedChange={(v) => alternar(d.destino, v === true)} />
              <span className="min-w-0 flex-1 truncate text-xs tabular-nums">
                {canal === 'sms' ? formatarTelefone(d.destino) : d.destino}
                {enviado && (
                  <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                    {travado && <Lock className="h-2.5 w-2.5" />}
                    {enviado}{travado && d.ultimo_envio_em ? ` · libera ${liberaEm(d.ultimo_envio_em)}` : ''}
                  </span>
                )}
              </span>
              <span className="flex flex-shrink-0 gap-1">
                {rotulosUnicos(d.fontes).slice(0, 2).map((r) => (
                  <span key={r} className="rounded border border-border px-1.5 text-[9.5px] text-muted-foreground">{r}</span>
                ))}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

interface ChipsProps {
  destinos: DestinoProposta[];
  marcados: string[];
  onChange: (marcados: string[]) => void;
  carregando: boolean;
  erro?: string | null;
}

/**
 * O "Para" da Proposta E-mail: um clique põe ou tira o endereço. A régua de
 * quem pode receber é a mesma da lista (fn_destinos_proposta_lead).
 */
export function ChipsDestinosEmail({ destinos, marcados, onChange, carregando, erro }: ChipsProps) {
  if (carregando) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> buscando os e-mails deste cliente…
      </span>
    );
  }
  if (erro) return <span className="text-[11px] text-destructive">{erro}</span>;
  if (destinos.length === 0) {
    return <span className="text-[11px] text-muted-foreground">Nenhum e-mail deste CNPJ no CRM. Cadastre o e-mail do Contato Real na aba Contato.</span>;
  }
  const contatoReal = destinos.some((d) => d.regra === 'contato_real');
  const regra = contatoReal ? 'Contato Real preenchido · vai só para ele' : 'Sem Contato Real · vai para os e-mails do CNPJ';
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1.5">
      {destinos.map((d) => {
        const marcado = marcados.includes(d.destino);
        const fonte = rotulosUnicos(d.fontes)[0];
        const enviado = d.ultimo_envio_em ? ` · proposta enviada ${dataHora(d.ultimo_envio_em)}` : '';
        return (
          <button
            key={d.destino}
            type="button"
            title={`${regra}${enviado}`}
            onClick={() => onChange(marcado ? marcados.filter((m) => m !== d.destino) : Array.from(new Set([...marcados, d.destino])))}
            className={`inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-[1px] text-[11px] transition-colors ${marcado
              ? 'border-[#B4808C] bg-[#F6ECEC] text-[#5B0A0D] dark:border-[#6C2236] dark:bg-[#2A1F1F] dark:text-[#E8C6CD]'
              : 'border-dashed border-border bg-card text-muted-foreground hover:border-[#9D2530]/50'}`}
          >
            {!marcado && <Plus className="h-3 w-3 flex-shrink-0" />}
            <span className="truncate">{d.destino}</span>
            {fonte && <span className="flex-shrink-0 text-[10px] text-muted-foreground">· {fonte}</span>}
          </button>
        );
      })}
    </span>
  );
}

interface SmsProps {
  modelos: ModeloSmsProposta[];
  texto: string;
  onTexto: (texto: string) => void;
}

export function EditorSmsProposta({ modelos, texto, onTexto }: SmsProps) {
  const tamanho = tamanhoSms(texto);
  const estourou = tamanho > LIMITE_SMS;
  return (
    <div className="space-y-2">
      <div className="flex gap-1.5">
        {modelos.map((m) => {
          const ativo = m.texto === texto;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => onTexto(m.texto)}
              className={`h-7 rounded-md border px-2.5 text-[11px] font-semibold transition-colors ${ativo ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card text-foreground hover:bg-muted'}`}
            >
              {m.rotulo}
            </button>
          );
        })}
      </div>
      <div className="relative">
        <textarea
          value={texto}
          onChange={(e) => onTexto(semAcento(e.target.value))}
          rows={3}
          className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 pr-16 text-xs leading-relaxed focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <span className={`absolute bottom-2 right-2 text-[10px] tabular-nums ${estourou ? 'font-bold text-destructive' : 'text-muted-foreground'}`}>
          {tamanho}/{LIMITE_SMS}
        </span>
      </div>
      <div className="rounded-lg bg-[#EFE9DF] p-3 dark:bg-[#1E1515]">
        <div className="max-w-[92%] whitespace-pre-wrap rounded-lg rounded-tl-none bg-[#DDE8DC] px-3 py-2 text-xs leading-relaxed text-[#1F2B1E] shadow-sm dark:bg-[#2E3A2D] dark:text-[#D9E4D7]">
          {texto.replace(/\{link\}/g, LINK_SMS_EXEMPLO)}
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground">
        sem acento e até {LIMITE_SMS} caracteres: assim chega numa mensagem só · o {'{link}'} abre o WhatsApp do dono do cliente, com o nome dele e o CNPJ
      </p>
    </div>
  );
}
